import { useCallback, useRef, useState } from 'react';
import {
  LINKEDIN_DOCUMENT_MAX_BYTES,
  MEDIA_UPLOAD_MAX_BYTES,
  MEDIA_UPLOAD_MAX_LABEL,
  isDocumentMime,
  isUploadableMime,
  type Asset,
} from '@mg/shared';
import { api, actionErrorText } from '../../lib/api';

/**
 * Why the Media Library would refuse this file, or null - the server's rules, checked before a byte is
 * sent: what it stores, the size cap, and for a PDF LinkedIn's 100 MB (decimal), which is a little
 * under the library's own cap and would otherwise be found out only after the whole upload.
 */
export function libraryUploadProblem(file: { type: string; size: number }): string | null {
  if (!isUploadableMime(file.type)) return 'Unsupported file type';
  if (file.size > MEDIA_UPLOAD_MAX_BYTES) return `Larger than ${MEDIA_UPLOAD_MAX_LABEL}`;
  if (isDocumentMime(file.type) && file.size > LINKEDIN_DOCUMENT_MAX_BYTES) {
    return "PDFs can be up to 100 MB (LinkedIn's limit for a document post)";
  }
  return null;
}

export type UploadStatus = 'uploading' | 'done' | 'error';

export interface UploadItem {
  /** Stable across renders; the File itself is not a usable key when the same file is added twice. */
  id: string;
  name: string;
  sizeBytes: number;
  mimeType: string;
  status: UploadStatus;
  /** 0–1 while uploading. Null when the browser can't tell us (chunked body). */
  progress: number | null;
  /** Why it failed — shown in place of the metadata line. */
  error?: string;
  /** The created asset, once it lands. Lets the caller show a real thumbnail. */
  asset?: Asset;
}

/**
 * The upload queue for the Media Library.
 *
 * Deliberately owned by the PAGE rather than the modal, because closing the modal does not cancel
 * anything — a user who has started a 90 MB video and wants to keep browsing should be able to. If
 * this state lived in the modal it would be destroyed on close, and "uploads continue in the
 * background" would be a claim rather than a behaviour.
 */
export function useMediaUploads(options: {
  folderId: string | null;
  /** Called after each successful upload so the grid can pick it up as it happens. */
  onUploaded: () => void;
}) {
  const { folderId, onUploaded } = options;
  const [items, setItems] = useState<UploadItem[]>([]);
  /** One controller per item, so Cancel can abort in-flight uploads without touching finished ones. */
  const controllers = useRef(new Map<string, AbortController>());
  const nextId = useRef(0);

  const patch = useCallback((id: string, changes: Partial<UploadItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...changes } : it)));
  }, []);

  /**
   * Queue files. Invalid ones are added as error rows rather than dropped: a file that silently
   * disappears from the list reads as a bug, and the user needs to know WHICH of the five they
   * dragged is the problem.
   */
  const add = useCallback(
    (files: File[]) => {
      for (const file of files) {
        const id = `u${nextId.current++}`;
        const base: UploadItem = {
          id,
          name: file.name,
          sizeBytes: file.size,
          mimeType: file.type,
          status: 'uploading',
          progress: 0,
        };

        // Validated here, before a single byte is sent. The server enforces the same rules, but
        // finding out a 200 MB file is too big AFTER uploading it is the same rejection delivered
        // as badly as possible.
        const problem = libraryUploadProblem(file);
        if (problem) {
          setItems((prev) => [...prev, { ...base, status: 'error', error: problem }]);
          continue;
        }

        const controller = new AbortController();
        controllers.current.set(id, controller);
        setItems((prev) => [...prev, base]);

        void api
          .uploadMediaWithProgress(file, {
            folderId: folderId ?? undefined,
            signal: controller.signal,
            onProgress: (fraction) => patch(id, { progress: fraction }),
          })
          .then((asset) => {
            patch(id, { status: 'done', progress: 1, asset });
            onUploaded();
          })
          .catch((err: unknown) => {
            // An abort is the user's own Cancel; the row is being removed anyway, so it is not an
            // error to report back to them.
            if (err instanceof DOMException && err.name === 'AbortError') return;
            patch(id, { status: 'error', error: actionErrorText(err, 'Upload failed') });
          })
          .finally(() => {
            controllers.current.delete(id);
          });
      }
    },
    [folderId, onUploaded, patch],
  );

  /** Abort everything still in flight and drop those rows. Finished and failed rows are kept. */
  const cancelInFlight = useCallback(() => {
    for (const controller of controllers.current.values()) controller.abort();
    controllers.current.clear();
    setItems((prev) => prev.filter((it) => it.status !== 'uploading'));
  }, []);

  /** Forget the list. Does NOT abort — used when reopening the modal on a fresh session. */
  const clear = useCallback(() => setItems([]), []);

  const uploading = items.filter((it) => it.status === 'uploading').length;

  return {
    items,
    add,
    cancelInFlight,
    clear,
    uploading,
    hasCompleted: items.some((it) => it.status === 'done'),
  };
}
